
import { useEffect } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { AuthHeader } from "@/components/auth/AuthHeader";
import { AuthForm } from "@/components/auth/AuthForm";
import { AuthFooter } from "@/components/auth/AuthFooter";

const Auth = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const mode = searchParams.get("mode") || "signin";
  const selectedPlan = location.state?.selectedPlan;
  const type = searchParams.get("type");

  useEffect(() => {
    // Check for password recovery flow
    if (type === "recovery" && mode !== "update") {
      setSearchParams({ mode: "update" });
      return;
    }

    const { data: authListener } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (event === "SIGNED_IN" && session) {
          console.log('User signed in, checking flow...');
          
          try {
            // Check if this is a trial signup
            const wantsTrial = searchParams.get('trial') === 'true';
            
            if (wantsTrial) {
              // For trial users, go directly to welcome/assessment
              navigate("/welcome");
              return;
            }

            // Check for existing onboarding
            const { data: onboardingData } = await supabase
              .from("onboarding_responses")
              .select("*")
              .eq("user_id", session.user.id)
              .maybeSingle();

            if (!onboardingData) {
              navigate("/welcome");
              return;
            }

            // Check for subscription or trial
            const [subscriptionResult, trialResult] = await Promise.all([
              supabase
                .from("user_subscriptions")
                .select("*")
                .eq("user_id", session.user.id)
                .eq("status", "active")
                .maybeSingle(),
              supabase
                .from("free_trials")
                .select("*")
                .eq("user_id", session.user.id)
                .eq("status", "active")
                .maybeSingle()
            ]);

            if (subscriptionResult.data || trialResult.data) {
              navigate("/dashboard");
            } else {
              // No subscription or trial - show trial upgrade prompt
              navigate("/plan-selection?source=trial-expired");
            }
          } catch (error) {
            console.error("Error during auth state change:", error);
            navigate("/plan-selection");
          }
        } else if (event === "PASSWORD_RECOVERY") {
          setSearchParams({ mode: "update" });
        }
      }
    );

    return () => {
      authListener.subscription.unsubscribe();
    };
  }, [navigate, searchParams]);

  const setSearchParams = (params: Record<string, string>) => {
    const newParams = new URLSearchParams(searchParams);
    Object.entries(params).forEach(([key, value]) => {
      newParams.set(key, value);
    });
    navigate(`${location.pathname}?${newParams.toString()}`, { replace: true });
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4 bg-gray-50">
      <Button
        variant="ghost"
        className="absolute top-4 left-4 flex items-center gap-2 text-gray-600 hover:text-gray-900"
        onClick={() => navigate("/")}
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Home
      </Button>

      <div className="w-full max-w-md space-y-8">
        <AuthHeader 
          mode={mode as any} 
          selectedPlan={selectedPlan} 
        />
        <AuthForm 
          mode={mode as any} 
          selectedPlan={selectedPlan}
        />
        <AuthFooter mode={mode as any} />
      </div>
    </div>
  );
};

export default Auth;
